import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister?: () => void;
}

export default function LoginModal({ isOpen, onClose, onSwitchToRegister }: LoginModalProps) {
  const [form, setForm] = useState({ identifier: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { refreshProfile } = useAuth();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.identifier.trim()) newErrors.identifier = 'Introduce tu email o usuario';
    if (!form.password) newErrors.password = 'Introduce tu contraseña';
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
    if (loginError) setLoginError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setLoginError('');

    try {
      const trimmed = form.identifier.trim().replace(/^@/, '');
      const isEmail = /\S+@\S+\.\S+/.test(trimmed);

      let emailToUse = trimmed;

      if (!isEmail) {
        // Username lookup: query profiles table (anon policy allows this)
        const { data: profile } = await supabase
          .from('profiles')
          .select('email')
          .eq('username', trimmed)
          .maybeSingle();

        if (!profile?.email) {
          setLoginError('Usuario no encontrado. Verifica el nombre de usuario.');
          setLoading(false);
          return;
        }
        emailToUse = profile.email;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: emailToUse,
        password: form.password,
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setLoginError('Email o contraseña incorrectos. Verifica tus datos.');
        } else if (error.message.includes('Email not confirmed')) {
          setLoginError('Confirma tu email antes de iniciar sesión. Revisa tu bandeja de entrada.');
        } else {
          setLoginError(error.message);
        }
        return;
      }

      await refreshProfile();
      handleClose();
    } catch {
      setLoginError('Error inesperado. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({ identifier: '', password: '' });
    setErrors({});
    setLoginError('');
    setShowPassword(false);
    onClose();
  };

  const handleSwitchToRegister = () => {
    handleClose();
    onSwitchToRegister?.();
  };

  const inputClass = (field: string) =>
    `w-full bg-[#1a1d26] border ${errors[field] ? 'border-red-500' : 'border-[#2a2d3a]'} rounded-lg px-4 py-3 text-white text-sm placeholder-[#4a5568] focus:outline-none focus:border-[#00e87a] transition-colors`;

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-sm bg-[#0d0f14] border border-[#2a2d3a] rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-7 pb-4 border-b border-[#2a2d3a]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-[#00e87a]/10 rounded-lg">
                <i className="ri-login-box-line text-[#00e87a] text-lg"></i>
              </div>
              <div>
                <h2 className="text-white font-bold text-lg leading-tight">Iniciar sesión</h2>
                <p className="text-[#6b7280] text-xs">Accede a tu cuenta de Apuestamente</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            {loginError && (
              <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <p className="text-red-400 text-xs leading-relaxed">{loginError}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-[#a8b2c1] mb-1.5">
                Correo electrónico
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#4a5568]">
                  <i className="ri-mail-line text-sm"></i>
                </div>
                <input
                  type="text"
                  name="identifier"
                  value={form.identifier}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  className={`${inputClass('identifier')} pl-9`}
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {errors.identifier && <p className="text-red-400 text-xs mt-1">{errors.identifier}</p>}
            </div>

            {/* Contraseña */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-[#a8b2c1]">
                  Contraseña
                </label>
                <button
                  type="button"
                  className="text-xs text-[#00e87a] hover:underline cursor-pointer"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#4a5568]">
                  <i className="ri-lock-line text-sm"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Tu contraseña"
                  className={`${inputClass('password')} pl-9 pr-10`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#4a5568] hover:text-[#a8b2c1] transition-colors cursor-pointer"
                >
                  <i className={`text-sm ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                </button>
              </div>
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#00e87a] text-black text-sm font-bold rounded-lg hover:bg-[#00d070] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line text-base"></i>
                  Iniciar sesión
                </>
              )}
            </button>

            {/* Register link */}
            <p className="text-center text-xs text-[#6b7280]">
              ¿No tienes cuenta?{' '}
              <button
                type="button"
                onClick={handleSwitchToRegister}
                className="text-[#00e87a] font-semibold hover:underline cursor-pointer"
              >
                Regístrate gratis
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
